import React, { useState } from 'react';
import ProjectCard from '../cards/ProjectCard';
import SearchIcon from '../icons/SearchIcon';
import EmptyProjectState from '../emptyState/EmptyProjectState';
import { Project } from '../../types/project';
import './ProjectGrid.css';

interface ProjectGridProps {
  projects: Project[];
  onViewProject: (project: Project) => void;
  onExport?: (project: Project) => void;
  onRename?: (project: Project) => void;
  onDelete?: (project: Project) => void;
  onCreateProject?: () => void;
  showSearch?: boolean;
  searchPlaceholder?: string;
  sectionTitle?: string;
  limitInitialDisplay?: number;
}

const ProjectGrid: React.FC<ProjectGridProps> = ({
  projects,
  onViewProject,
  onExport,
  onRename,
  onDelete,
  onCreateProject,
  showSearch = true,
  searchPlaceholder = "Search Projects...",
  sectionTitle,
  limitInitialDisplay,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const filteredProjects = showSearch
    ? projects.filter((project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : projects;

  const shouldLimit = limitInitialDisplay && !showAll && !searchQuery;
  const displayedProjects = shouldLimit
    ? filteredProjects.slice(0, limitInitialDisplay)
    : filteredProjects;
  const remainingCount = shouldLimit ? filteredProjects.length - limitInitialDisplay : 0;

  return (
    <>
      <div className="project-grid-header">
        {sectionTitle && <h2 className="section-title">{sectionTitle}</h2>}

        {showSearch && projects.length > 0 && (
          <div className="search-container">
            <SearchIcon className="search-icon" size={16} />
            <input
              className="search-input"
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}


      </div>

      {filteredProjects.length === 0 && projects.length === 0 && onCreateProject ? (
        <EmptyProjectState onCreateProject={onCreateProject} />
      ) : filteredProjects.length === 0 ? (
        <div className="no-results">No projects found matching your search.</div>
      ) : (
        <>
          <div className="projects-grid-container">
            <div className="projects-grid-fade"></div>
            <div className="projects-grid">
              {displayedProjects.map((project) => (
                <ProjectCard
                  key={project.project_id}
                  project={project}
                  onClick={() => onViewProject(project)}
                  onExport={onExport}
                  onRename={onRename}
                  onDelete={onDelete}
                />
              ))}
            </div>
          </div>

          {remainingCount > 0 && (
            <button className="show-more-button" onClick={() => setShowAll(true)}>
              <span className="show-more-text">Show More Projects ({remainingCount})</span>
              <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.638672 9.42188C0.55013 9.33854 0.482422 9.24219 0.435547 9.13281C0.39388 9.02344 0.373047 8.90885 0.373047 8.78906C0.373047 8.61719 0.412109 8.46354 0.490234 8.32812C0.573568 8.1875 0.682943 8.07812 0.818359 8C0.953776 7.91667 1.10742 7.875 1.2793 7.875C1.5293 7.875 1.74805 7.96615 1.93555 8.14844L7.67773 14.0234H6.91992L12.6543 8.14844C12.8418 7.96615 13.0605 7.875 13.3105 7.875C13.4824 7.875 13.6361 7.91667 13.7715 8C13.9069 8.07812 14.0137 8.1875 14.0918 8.32812C14.1751 8.46354 14.2168 8.61719 14.2168 8.78906C14.2168 9.03906 14.1283 9.25 13.9512 9.42188L8.00586 15.5078C7.90169 15.612 7.78971 15.6901 7.66992 15.7422C7.55534 15.7995 7.43034 15.8281 7.29492 15.8281C7.03451 15.8281 6.79753 15.724 6.58398 15.5156L0.638672 9.42188ZM0.638672 2.44531C0.55013 2.35677 0.482422 2.26042 0.435547 2.15625C0.39388 2.04688 0.373047 1.92969 0.373047 1.80469C0.373047 1.63281 0.412109 1.47917 0.490234 1.34375C0.573568 1.20312 0.682943 1.09375 0.818359 1.01562C0.953776 0.932292 1.10742 0.890625 1.2793 0.890625C1.5293 0.890625 1.74805 0.981771 1.93555 1.16406L7.67773 7.04688H6.91992L12.6543 1.16406C12.8418 0.981771 13.0605 0.890625 13.3105 0.890625C13.4824 0.890625 13.6361 0.932292 13.7715 1.01562C13.9069 1.09375 14.0137 1.20312 14.0918 1.34375C14.1751 1.47917 14.2168 1.63281 14.2168 1.80469C14.2168 2.05469 14.1283 2.26562 13.9512 2.4375L8.00586 8.52344C7.9069 8.63281 7.79753 8.71354 7.67773 8.76562C7.55794 8.81771 7.43034 8.84375 7.29492 8.84375C7.03451 8.84375 6.79753 8.73958 6.58398 8.53125L0.638672 2.44531Z" fill="url(#paint0_linear_30_10713)"/>
                <defs>
                  <linearGradient id="paint0_linear_30_10713" x1="-1.70508" y1="8.5" x2="16.2949" y2="8.5" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#00D650"/>
                    <stop offset="1" stopColor="#0081FE"/>
                  </linearGradient>
                </defs>
              </svg>
            </button>
          )}
        </>
      )}
    </>
  );
};

export default ProjectGrid;
